import { bindActionCreators } from '@reduxjs/toolkit';
import { WebAuth } from 'auth0-js';
import React, { useEffect } from 'react';
import {
	Navigate,
	Routes,
	Route,
	useNavigate
} from 'react-router-dom';
import { Loader } from 'vio-components';
import {
	getAuth0ClientId,
	getAuth0Domain,
	isAuth0Authenticated,
	usePrevious,
	useReduxState
} from 'vio-libs';

import { Header } from './components';
import Gateway from './Gateway';
import { IAuthState, IVaporIO } from './interfaces';
import {
	AddUpdate,
	Assets,
	Delete,
	Map
} from './pages';
import {
	requestRemoveServerSideCookie,
	resetAuth0,
	resetMapFeatures
} from './redux';
import { useAppDispatch } from './redux/hooks';

declare const vaporio: IVaporIO;

const App: React.FC = () => {
	// HOOKS
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	// REDUX STATE
	const {
		requestingRemoveServerSideCookie,
		requestingSetServerSideCookie,
		unauthorized
	} = useReduxState<IAuthState>('auth');
	const previousUnauthorizedState = usePrevious(unauthorized);
	const previousRequestingRemoveServerSideCookie = usePrevious(requestingRemoveServerSideCookie);
	// REDUX ACTIONS
	const requestRemoveServerSideCookieDispatch = bindActionCreators(requestRemoveServerSideCookie, dispatch);
	const resetAuth0Dispatch = bindActionCreators(resetAuth0, dispatch);
	const resetMapFeaturesDispatch = bindActionCreators(resetMapFeatures, dispatch);
	// VARIABLES
	const authenticated = isAuth0Authenticated();
	const webAuth = new WebAuth({
		clientID: getAuth0ClientId(vaporio),
		domain: getAuth0Domain(vaporio)
	});

	useEffect(() => {
		if (unauthorized
			&& (unauthorized !== previousUnauthorizedState)
			&& !requestingRemoveServerSideCookie) {
			requestRemoveServerSideCookieDispatch();
		}
	}, [ unauthorized ]);

	// upon logout complete clear app state and return to login page
	useEffect(() => {
		if (previousRequestingRemoveServerSideCookie && !requestingRemoveServerSideCookie) {
			resetAuth0Dispatch();
			resetMapFeaturesDispatch();
			webAuth.logout({ returnTo: `${window.location.origin}/auth` });
			navigate('/');
		}
	}, [ requestingRemoveServerSideCookie ]);

	const renderView = () => {
		if (requestingRemoveServerSideCookie || requestingSetServerSideCookie) {
			return (
				<Loader />
			);
		}

		if (authenticated && !unauthorized) {
			return (
				<>
					<Header />

					<div className='view'>
						<Routes>
							<Route path='map' element={ <Map /> } />

							<Route path='assets'>
								<Route index element={ <Assets /> } />
								<Route path='add' element={ <AddUpdate /> } />
								<Route path=':id/edit' element={ <AddUpdate /> } />
								<Route path=':id/upload' element={ <AddUpdate /> } />
								<Route path=':id/delete' element={ <Delete /> } />
							</Route>

							<Route path='layers'>
								<Route path=':id/delete' element={ <Delete /> } />
							</Route>

							<Route path='*' element={ <Navigate replace to={ '/assets' } /> } />
						</Routes>
					</div>
				</>
			);
		}

		return <Gateway />;
	};

	return (
		<div id='kinetic-grid-container'>
			{ renderView() }
		</div>
	);
};

export default App;
